import axios from "axios";
import { buildBrightnessAzureOutput } from "./installationBrightnessUtils";
import {
  buildDefaultImageReadingsAzureOutput,
  DEFAULT_IMAGE_READINGS_ROTATION_ANGLE,
} from "./installationImageReadingsDefaults";
import {
  AZURE_RAW_IMAGE_QUERY,
  buildCropAzureOutput,
  buildPhotoAzureOutput,
  getBaseCameraId,
  getLeftCameraId,
  getRightCameraId,
  mergeAzureConfig,
  normalizeSimProvider,
} from "./installationConfigUtils";

let simCardListCache = null;

export async function getSimCardProvider(iccid) {
  if (!iccid) {
    return null;
  }

  try {
    if (!simCardListCache) {
      const response = await axios.get(
        `https://cameraanalysis.blob.core.windows.net/camera-analysis/sim-cards/sim_card.json${AZURE_RAW_IMAGE_QUERY}`,
      );
      simCardListCache = Array.isArray(response.data) ? response.data : [];
    }

    const entry = simCardListCache.find((item) => item.iccid === iccid);
    return normalizeSimProvider(entry?.provider);
  } catch {
    return null;
  }
}

async function getCamera(api, token, cameraId) {
  const response = await axios.get(`${api}camera/${cameraId}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  return response.data?.data;
}

export async function loadCameraPair(api, token, routeCameraId) {
  const leftId = getLeftCameraId(routeCameraId);
  const rightId = getRightCameraId(routeCameraId);
  const [leftCamera, rightCamera] = await Promise.all([
    getCamera(api, token, leftId).catch(() => null),
    getCamera(api, token, rightId).catch(() => null),
  ]);
  return { leftCamera, rightCamera, hasRightCamera: Boolean(rightCamera) };
}

export async function getAzureConfig(api, token, cameraId) {
  const response = await axios.get(
    `${api}azureconfig/${getBaseCameraId(cameraId)}`,
    {
      headers: { authorization: `Bearer ${token}` },
    },
  );
  return response.data || {};
}

export async function writeAzureConfig(
  api,
  token,
  cameraId,
  currentConfig,
  partial,
) {
  const output = mergeAzureConfig(currentConfig, partial);
  const response = await axios.post(
    `${api}writeazureconfig/${getBaseCameraId(cameraId)}`,
    { output },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return { response, output };
}

export async function updateCameraStatus(api, token, cameraId, status) {
  const leftId = getLeftCameraId(cameraId);
  return axios.post(
    `${api}camera/update/${leftId}`,
    {
      _id: leftId,
      status,
      side: "Left",
    },
    { headers: { authorization: `Bearer ${token}` } },
  );
}

export async function updateCameraRotation(
  api,
  token,
  cameraId,
  imageRotationAngle,
  side,
) {
  return axios.post(
    `${api}camera/update/${cameraId}`,
    {
      _id: cameraId,
      image_rotation_angle: imageRotationAngle,
      side: side === "right" ? "Right" : "Left",
    },
    { headers: { authorization: `Bearer ${token}` } },
  );
}

export async function saveCropMaskConfig(
  api,
  token,
  cameraId,
  currentConfig,
  side,
  polygons,
) {
  const output = buildCropAzureOutput(currentConfig, side, polygons);
  const response = await axios.post(
    `${api}writeazureconfig/${getBaseCameraId(cameraId)}`,
    { output },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return { response, output };
}

export async function savePhotoSideConfig(
  api,
  token,
  cameraId,
  currentConfig,
  side,
  enabled,
) {
  const output = buildPhotoAzureOutput(currentConfig, side, enabled);
  const response = await axios.post(
    `${api}writeazureconfig/${getBaseCameraId(cameraId)}`,
    { output },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return { response, output };
}

export async function saveBrightnessConfig(
  api,
  token,
  cameraId,
  currentConfig,
  brightnessState,
) {
  const output = buildBrightnessAzureOutput(currentConfig, brightnessState);
  const response = await axios.post(
    `${api}writeazureconfig/${getBaseCameraId(cameraId)}`,
    { output },
    { headers: { authorization: `Bearer ${token}` } },
  );
  return { response, output };
}

export async function resetImageReadingsToDefaults(
  api,
  token,
  routeCameraId,
  configData,
  leftCamera,
  rightCamera,
) {
  const output = buildDefaultImageReadingsAzureOutput(configData);
  const response = await axios.post(
    `${api}writeazureconfig/${getBaseCameraId(routeCameraId)}`,
    { output },
    { headers: { authorization: `Bearer ${token}` } },
  );

  const rotationUpdates = [];
  if (leftCamera?._id) {
    rotationUpdates.push(
      updateCameraRotation(
        api,
        token,
        leftCamera._id,
        DEFAULT_IMAGE_READINGS_ROTATION_ANGLE,
        "left",
      ),
    );
  }
  if (rightCamera?._id) {
    rotationUpdates.push(
      updateCameraRotation(
        api,
        token,
        rightCamera._id,
        DEFAULT_IMAGE_READINGS_ROTATION_ANGLE,
        "right",
      ),
    );
  }
  await Promise.all(rotationUpdates);

  return { response, output };
}

export async function updateSiteProperties(api, token, payload) {
  return axios.post(
    `${api}camera/siteProperties/update/${payload._id}`,
    payload,
    {
      headers: { authorization: `Bearer ${token}` },
    },
  );
}

export async function getContainersEditList(api, token, corporationId) {
  const response = await axios.get(`${api}containers/edit`, {
    headers: { authorization: `Bearer ${token}` },
    params: { corporation: corporationId },
  });
  return response.data?.containersList || [];
}

export async function updateCameraContainers(
  api,
  token,
  cameraId,
  side,
  containerObjectIdList,
) {
  return axios.post(
    `${api}camera/update/${cameraId}`,
    {
      _id: cameraId,
      side: side === "right" ? "Right" : "Left",
      containerObjectIdList,
    },
    { headers: { authorization: `Bearer ${token}` } },
  );
}

export async function getAddressNameList(api, token, corporationId) {
  const response = await axios.get(`${api}address/getAddressNameList`, {
    headers: { authorization: `Bearer ${token}` },
    params: { corporationId },
  });
  return response.data?.addressNameList || [];
}

export async function getCameraLogDetails(api, token, cameraId, filename) {
  const response = await axios.get(
    `${api}akanthascameralogdetails/${getBaseCameraId(cameraId)}`,
    {
      headers: { authorization: `Bearer ${token}` },
      params: { filename },
    },
  );
  return response.data;
}

export async function getAzureCycleLogAndImage(
  api,
  token,
  cameraId,
  numberOfDaysBack = 1,
) {
  const response = await axios.get(
    `${api}azurecyclelogandimage/${getBaseCameraId(cameraId)}`,
    {
      headers: { authorization: `Bearer ${token}` },
      params: { numberOfDaysBack },
    },
  );
  return response.data || [];
}

export async function getCameraMonitoringHistory(routeCameraId) {
  const baseId = getBaseCameraId(routeCameraId);
  const response = await axios.get(
    `https://cameraanalysis.blob.core.windows.net/camera-analysis/camera-${baseId}/output-monitoring/data-monitoring-7-days.json${AZURE_RAW_IMAGE_QUERY}`,
  );
  return Array.isArray(response.data) ? response.data : [];
}
